import * as errors from '@attain-apps/errors'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Feature } from '../../feature'
import type { Op } from '../op'

import { Config } from './config'
import { OpManager } from './op-manager'
import { createRelay } from './relay'

// TODO: document, based on https://ngrx.io/guide/store/action-groups, but with filters, op types, and other useful features

// TODO: this file is a bit bloated and should be better organized (i.e.
// probably split into multiple files)

export type Group<TConfig extends Config.Constraint> = {
  [TOpName in keyof TConfig as TOpName extends string
    ? TOpName
    : never]: TOpName extends string
    ? Group.Entry<TOpName, TConfig[TOpName]>
    : never
}
export namespace Group {
  export type Entry<
    TOpName extends string,
    TOpConfig extends Config.StandaloneOp<any> | Config.RequestOp<any>,
  > =
    TOpConfig extends Config.StandaloneOp<any>
      ? OpManager.FromConfig<TOpName, TOpConfig>
      : TOpConfig extends Config.RequestOp<any>
        ? OpManager.RequestGroup.FromConfig<TOpName, TOpConfig>
        : never

  export function create<
    TState,
    TSelectors,
    const TConfig extends Config.Constraint<TState>,
  >(
    feature: Feature<TState, TSelectors, TConfig>,
    config: TConfig,
  ): Group<TConfig>
  export function create(
    feature: Feature.Default,
    config: Config.Constraint,
  ): Group<Config.Constraint> {
    const group: Group<Config.Constraint> = {}
    for (const opName in config) {
      const opConfig = config[opName]
      const opConfigType = opConfig[Config.OP_TYPE_KEY]
      // TODO: some of the below should maybe be split into helper functions in
      // op-manager.ts, producer.ts, etc.
      switch (opConfigType) {
        case 'standalone':
          group[opName] = OpManager.create(
            feature,
            { name: opName, status: undefined },
            opConfig,
          ) satisfies OpManager<any, Op<string, undefined>>
          break
        case 'request':
          {
            const {
              input: transformInput,
              output: transformOutput,
              reducersDeprecated,
              reducers,
            } = opConfig
            function createOpManager<TStatus extends Op.Status>(
              status: TStatus,
              transformData: Config.StandaloneOp<
                any,
                Op.Status.Data.Input<
                  TStatus,
                  unknown,
                  unknown,
                  unknown,
                  unknown
                >,
                Op.Status.Data<TStatus, unknown, unknown>
              >['data'],
            ) {
              return OpManager.create(
                feature,
                { name: opName, status: status },
                {
                  data: transformData,
                  reducer: reducers?.[status],
                  reducerDeprecated: reducersDeprecated?.[status],
                },
              )
            }
            group[opName] = {
              cancellation: createOpManager('cancellation', data => ({
                reason: data?.reason ?? 'Unknown reason',
                request: data?.request ?? transformInput(data?.input),
              })),
              error: createOpManager('error', data => ({
                error: data?.error ?? new errors.UnexpectedError(),
                request: data?.request ?? transformInput(data?.input),
              })),
              from(...args) {
                return {
                  ...this.request.from(...args),
                  /**
                   * TODO:
                   */
                  execute: data => {
                    const requestAction = this.request
                      .from(...args)
                      .dispatch(data)
                    const response$ = rxjs.merge(
                      // Success
                      this.success.observe().pipe(
                        requestAction.relatedBy({ request: true }),
                        rxjs.map(action => action.data.response),
                      ),
                      // Error
                      this.error.observe().pipe(
                        requestAction.relatedBy({ request: true }),
                        rxjs.map(action => {
                          throw action.data.error
                        }),
                      ),
                      // Cancellation
                      this.cancellation.observe().pipe(
                        requestAction.relatedBy({ request: true }),
                        rxjs.map(action => {
                          // TODO: this should use a special CancellationError class
                          throw new Error(
                            `${opName} cancelled. ${action.data.reason}`,
                          )
                        }),
                      ),
                    )
                    let promise: Promise<unknown> | undefined
                    return {
                      asObservable: response$,
                      get asPromise() {
                        // Lazily created, only when requested
                        return (promise ??= rxjs.firstValueFrom(response$))
                      },
                    }
                  },
                }
              },
              /**
               *
               */
              relay(
                mapper: Parameters<typeof this.relay>[0],
                options?: OpManager.RequestGroup.relay.Options,
              ) {
                const action$ = createRelay(this, mapper, options)
                return {
                  asObservable: action$,
                  init: () =>
                    action$.subscribe(action => feature.store.dispatch(action)),
                }
              },
              request: createOpManager('request', input =>
                transformInput(input),
              ),
              success: createOpManager('success', data => ({
                request: data?.request ?? transformInput(data?.input),
                response: transformOutput(data?.response),
              })),
            } satisfies OpManager.RequestGroup<
              any,
              string,
              unknown,
              unknown,
              unknown,
              unknown
            > as OpManager.RequestGroup<any, string, any, any, any, any>
          }
          break
        default:
          throw TypeAsserter.assertIsNever(opConfigType)
      }
    }
    return group
  }
}
